import { z } from 'zod';

export const QuoteCreationSchema = z.object({
  tenant_id: z.string().uuid("Invalid tenant ID"),
  user_id: z.string().uuid("Invalid user ID"),
  project_type_id: z.string().uuid("Project type is required"),
  client_info: z.object({
    name: z.string().min(1, "Client name is required").max(100, "Client name too long"),
    projectName: z.string().min(1, "Project name is required").max(100, "Project name too long"),
    bdmId: z.string().uuid().nullable().optional(),
    email: z.string().optional().nullable(),
    phone: z.string().optional().nullable(),
    address: z.string().optional().nullable(),
    gstNumber: z.string().optional().nullable(),
  }),
  scope_selections: z.array(z.object({
    type_value: z.string().min(1, "Scope type is required"),
    count: z.number().min(1, "Scope count must be at least 1").max(100, "Scope count too high"),
  })).optional().default([]),
});

export const QuoteUpdateSchema = z.object({
  id: z.string().uuid(),
  client_info: z.object({
    name: z.string().min(1, "Client name is required").max(100),
    projectName: z.string().min(1, "Project name is required").max(100),
    bdmId: z.string().uuid().nullable().optional(),
  }).optional(),
  title: z.string().min(1, "Title is required").max(200).optional(),
});

export type QuoteCreationParams = z.infer<typeof QuoteCreationSchema>;
export type QuoteUpdateParams = z.infer<typeof QuoteUpdateSchema>;

// Helper function to generate deterministic title
export const generateQuoteTitle = (clientName: string, projectName: string): string => {
  const cleanClientName = clientName.trim();
  const cleanProjectName = projectName.trim();
  
  if (!cleanClientName && !cleanProjectName) {
    return 'Untitled Quote';
  }
  
  if (!cleanClientName) {
    return cleanProjectName;
  }
  
  if (!cleanProjectName) {
    return cleanClientName;
  }
  
  return `${cleanClientName} - ${cleanProjectName}`;
};

// Validation helper for quote creation
export const validateQuoteCreation = (params: unknown): QuoteCreationParams => {
  try {
    return QuoteCreationSchema.parse(params);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.errors.map(err => `${err.path.join('.')}: ${err.message}`);
      throw new Error(`Validation failed: ${errorMessages.join(', ')}`);
    }
    throw error;
  }
};
